import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Breadcrumbs, type Crumb } from '../../components/ui/index.js';
import { usePublishCrumbs } from '../../components/ui/shell-slot.js';

/**
 * The record-page and register pieces the clients-and-orders pages share
 * (approved redesign, D278).
 *
 * Module-local on purpose: the shared library (`components/ui`) is owned by
 * the design-system pass, and these are the prototype's "record bar", "facts",
 * "chain of records" and "view tabs" shaped for the pages in this workspace —
 * clients, projects, quotations, pricing agreements, work orders and finance.
 * Every piece takes text ALREADY TRANSLATED and holds no string of its own
 * except through `t()`.
 *
 * Nothing here decides anything. A badge, a number, a link: each is rendered
 * from what the page already knows, and nothing is fetched or derived here.
 */

/** A record number, read off paper and typed into search boxes: always LTR. */
export function RecordNumber({
  children,
  to,
}: {
  readonly children: ReactNode;
  /** When given, the number is the link to its record. */
  readonly to?: string;
}) {
  const className = 'measure whitespace-nowrap font-semibold';
  return to ? (
    <Link
      to={to}
      dir="ltr"
      className={`${className} text-primary-700 underline-offset-2 hover:underline`}
    >
      {children}
    </Link>
  ) : (
    <span dir="ltr" className={`${className} text-default-900`}>
      {children}
    </span>
  );
}

/**
 * The top of a record page: the record number and its status on one line,
 * the title beneath it, the key context (client, project, work order) as
 * links, and the record's actions at the trailing end — the primary next
 * action among them, never more than one primary.
 *
 * Breadcrumbs go to the shell's top bar exactly as `PageHeader` sends them;
 * outside the shell (a component test) they are drawn here.
 */
export function RecordHeader({
  number,
  badges,
  title,
  context,
  actions,
  breadcrumbs,
}: {
  /** The record's own number. Rendered LTR in the measure face. */
  readonly number?: string;
  /** Status badges, beside the number. */
  readonly badges?: ReactNode;
  /** Already translated, or the record's own entered name. */
  readonly title: string;
  /** Context links and facts under the title (`ContextItem`s). */
  readonly context?: ReactNode;
  readonly actions?: ReactNode;
  readonly breadcrumbs?: readonly Crumb[];
}) {
  const inShell = usePublishCrumbs(breadcrumbs);
  return (
    <header className="mb-stack rounded-large border border-default-200 bg-white px-4 py-3">
      {breadcrumbs && !inShell ? (
        <div className="mb-2">
          <Breadcrumbs items={breadcrumbs} />
        </div>
      ) : null}
      <div className="flex flex-wrap items-start gap-x-stack gap-y-3">
        <div className="min-w-0 grow basis-64">
          {number || badges ? (
            <div className="flex flex-wrap items-center gap-2 text-body">
              {number ? <RecordNumber>{number}</RecordNumber> : null}
              {badges}
            </div>
          ) : null}
          <h1 className="mt-0.5 text-section font-bold text-default-900" dir="auto">
            {title}
          </h1>
          {context ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-caption text-default-600">
              {context}
            </div>
          ) : null}
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center gap-inline">{actions}</div>
        ) : null}
      </div>
    </header>
  );
}

/** One piece of context under a record's title: a label and its value. */
export function ContextItem({
  label,
  children,
}: {
  /** Already translated. */
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <span>{label}:</span>
      <span className="min-w-0 text-default-800">{children}</span>
    </span>
  );
}

/** A link to a related record, in the page's ink. */
export function RecordLink({
  to,
  children,
  isNumber = false,
}: {
  readonly to: string;
  readonly children: ReactNode;
  /** A record number: LTR, in the measure face. */
  readonly isNumber?: boolean;
}) {
  return (
    <Link
      to={to}
      {...(isNumber ? { dir: 'ltr' } : { dir: 'auto' })}
      className={`text-primary-700 underline-offset-2 hover:underline ${
        isNumber ? 'measure whitespace-nowrap' : ''
      }`}
    >
      {children}
    </Link>
  );
}

/**
 * The record body: a main column and, on a wide screen, a narrow side column
 * for the facts, the chain of related records and the recent history. Below
 * the `lg` breakpoint the side column follows the main one.
 *
 * `min-w-0` on both tracks: a grid track defaults to max-content, and one wide
 * table would otherwise push the page past a phone's width.
 */
export function RecordGrid({
  children,
  side,
}: {
  readonly children: ReactNode;
  readonly side?: ReactNode;
}) {
  return (
    <div
      className={`grid min-w-0 grid-cols-1 items-start gap-gutter ${
        side ? 'lg:grid-cols-[minmax(0,1fr)_19rem]' : ''
      }`}
    >
      <div className="flex min-w-0 flex-col gap-gutter">{children}</div>
      {side ? <div className="flex min-w-0 flex-col gap-gutter">{side}</div> : null}
    </div>
  );
}

export interface Fact {
  /** Already translated. */
  readonly label: string;
  readonly value: ReactNode;
}

/**
 * Label and value pairs. In the side column they read as two columns per
 * row; `layout="grid"` spreads them across the main column instead.
 */
export function Facts({
  items,
  layout = 'rows',
}: {
  readonly items: readonly (Fact | null | false)[];
  readonly layout?: 'rows' | 'grid';
}) {
  const facts = items.filter((item): item is Fact => Boolean(item));
  if (layout === 'grid') {
    return (
      <dl className="grid gap-x-gutter gap-y-stack sm:grid-cols-2 lg:grid-cols-3">
        {facts.map((fact) => (
          <div key={fact.label} className="min-w-0">
            <dt className="text-caption text-default-600">{fact.label}</dt>
            <dd className="mt-0.5 break-words text-body text-default-900">{fact.value}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return (
    <dl className="flex flex-col">
      {facts.map((fact) => (
        <div
          key={fact.label}
          className="grid grid-cols-[minmax(6rem,40%)_minmax(0,1fr)] gap-2 py-1"
        >
          <dt className="text-caption text-default-600">{fact.label}</dt>
          <dd className="min-w-0 break-words text-body text-default-900">{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface ChainLink {
  /** What kind of record it is. Already translated. */
  readonly label: string;
  /** Its number or name, as the page already knows it. */
  readonly value: ReactNode;
}

/**
 * The chain of related records — client, project, quotation, work order and
 * onward — as far as this page already knows it. A link the reader may not
 * follow is shown as plain text, never hidden.
 */
export function RecordChain({ items }: { readonly items: readonly (ChainLink | null | false)[] }) {
  const links = items.filter((item): item is ChainLink => Boolean(item));
  return (
    <ul className="flex flex-col">
      {links.map((link, index) => (
        <li
          key={`${index}:${link.label}`}
          className="grid grid-cols-[minmax(6rem,40%)_minmax(0,1fr)] items-baseline gap-2 py-1"
        >
          <span className="text-caption text-default-600">{link.label}</span>
          <span className="min-w-0 break-words text-body text-default-900">{link.value}</span>
        </li>
      ))}
    </ul>
  );
}

export interface ViewTab {
  /** `''` is the unfiltered view. */
  readonly value: string;
  /** Already translated. */
  readonly label: string;
}

/**
 * The register's status views: one tab per state, the first being every
 * record. A filter, not navigation — choosing a view narrows what is SHOWN.
 *
 * Arrow keys move between views the way the reader reads: under Arabic the
 * left arrow moves forward.
 */
export function ViewTabs({
  label,
  value,
  options,
  onChange,
}: {
  /** Names the set of views. Already translated. */
  readonly label: string;
  readonly value: string | null;
  readonly options: readonly ViewTab[];
  readonly onChange: (value: string | null) => void;
}) {
  const { i18n } = useTranslation();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const current = value ?? '';
  const isRtl = i18n.dir() === 'rtl';

  const move = (event: KeyboardEvent, index: number) => {
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const back = isRtl ? 'ArrowRight' : 'ArrowLeft';
    let next = -1;
    if (event.key === forward) next = (index + 1) % options.length;
    else if (event.key === back) next = (index - 1 + options.length) % options.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = options.length - 1;
    if (next < 0) return;
    event.preventDefault();
    const option = options[next];
    if (!option) return;
    onChange(option.value === '' ? null : option.value);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className="mb-stack flex flex-wrap gap-x-1 border-b border-default-200"
    >
      {options.map((option, index) => {
        const isSelected = option.value === current;
        return (
          <button
            key={option.value || 'all'}
            ref={(element) => {
              refs.current[index] = element;
            }}
            type="button"
            role="tab"
            aria-selected={isSelected}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onChange(option.value === '' ? null : option.value)}
            onKeyDown={(event) => move(event, index)}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 pb-2 pt-1.5 text-body font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary-600 ${
              isSelected
                ? 'border-warning-500 text-default-900'
                : 'border-transparent text-default-600 hover:text-default-900'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The money a table sums, at the trailing edge beneath it: each subtotal on
 * its own line and the total last, under a rule. Every figure is the
 * server's; nothing is added up here.
 */
export function Totals({
  rows,
  total,
}: {
  readonly rows: readonly Fact[];
  readonly total: Fact;
}) {
  return (
    <div className="mt-stack flex justify-end border-t border-default-200 pt-stack">
      <dl className="grid w-full max-w-sm grid-cols-[minmax(0,1fr)_auto] gap-x-gutter gap-y-1 text-body">
        {rows.map((row) => (
          <div key={row.label} className="contents">
            <dt className="text-default-600">{row.label}</dt>
            <dd className="measure text-end text-default-900" dir="ltr">
              {row.value}
            </dd>
          </div>
        ))}
        <div className="contents">
          <dt className="border-t border-default-200 pt-1 font-semibold text-default-900">
            {total.label}
          </dt>
          <dd
            className="measure border-t border-default-200 pt-1 text-end font-semibold text-default-900"
            dir="ltr"
          >
            {total.value}
          </dd>
        </div>
      </dl>
    </div>
  );
}
